import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, RefreshCw, ChevronLeft, ChevronRight, FolderOpen, Inbox, Send, Archive, Trash2, AlertTriangle, Settings, Unplug } from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';
import { useAuth } from '../../../context/AuthContext';
import { apiGet, apiPost, apiDelete } from '../../../utils/api';
import ConnectOutlookButton from '../../email/ConnectOutlookButton';
import EmailList from '../../email/EmailList';
import EmailAIChat from '../../email/EmailAIChat';

const FOLDER_ICONS = {
    inbox: Inbox,
    sentitems: Send,
    archive: Archive,
    deleteditems: Trash2,
    junkemail: AlertTriangle,
};

export default function EmailDashboard() {
    const { theme } = useTheme();
    const { currentUser, canAccessPdfExtraction } = useAuth();

    const [connected, setConnected] = useState(false);
    const [authorized, setAuthorized] = useState(null); // null = loading
    const [emails, setEmails] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedEmails, setSelectedEmails] = useState([]);
    const [folders, setFolders] = useState([]);
    const [currentFolder, setCurrentFolder] = useState('inbox');
    const [searchQuery, setSearchQuery] = useState('');
    const [emailsLoading, setEmailsLoading] = useState(false);
    const [analyzeLoading, setAnalyzeLoading] = useState(false);
    const [fullEmails, setFullEmails] = useState({});
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const settingsRef = useRef(null);
    const [skip, setSkip] = useState(0);
    const perPage = 20;

    // Close settings dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (settingsRef.current && !settingsRef.current.contains(event.target)) {
                setSettingsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Check authorization and connection status
    useEffect(() => {
        checkStatus();
    }, []);

    async function checkStatus() {
        try {
            const response = await apiGet('/outlook/status');
            if (response.ok) {
                const data = await response.json();
                setConnected(data.connected);
                setAuthorized(true);
            } else if (response.status === 403) {
                setAuthorized(false);
            }
        } catch {
            setAuthorized(false);
        }
    }

    // Fetch emails when connected / folder / search changes
    useEffect(() => {
        if (connected) {
            fetchEmails();
            fetchFolders();
        }
    }, [connected, currentFolder]);

    const fetchEmails = useCallback(async (overrideSkip, overrideSearch) => {
        setEmailsLoading(true);
        try {
            const s = overrideSkip ?? skip;
            const query = overrideSearch ?? searchQuery;
            let endpoint = `/outlook/emails?folder=${currentFolder}&skip=${s}&per_page=${perPage}`;

            // Parse search for from: syntax
            let searchText = query;
            const fromMatch = query.match(/from:(\S+)/i);
            if (fromMatch) {
                endpoint += `&from=${encodeURIComponent(fromMatch[1])}`;
                searchText = query.replace(/from:\S+/i, '').trim();
            }
            if (searchText) {
                endpoint += `&search=${encodeURIComponent(searchText)}`;
            }

            const response = await apiGet(endpoint);
            if (response.ok) {
                const data = await response.json();
                setEmails(data.value || []);
            }
        } catch (err) {
            console.error('Failed to fetch emails:', err);
        } finally {
            setEmailsLoading(false);
        }
    }, [currentFolder, searchQuery, skip]);

    async function fetchFolders() {
        try {
            const response = await apiGet('/outlook/folders');
            if (response.ok) {
                const data = await response.json();
                setFolders(data.value || []);
            }
        } catch {
            // Folders are non-critical
        }
    }

    async function fetchFullEmail(emailId) {
        if (fullEmails[emailId]) return;
        try {
            const response = await apiGet(`/outlook/emails/${emailId}`);
            if (response.ok) {
                const data = await response.json();
                setFullEmails(prev => ({ ...prev, [emailId]: data }));
            }
        } catch (err) {
            console.error('Failed to fetch email:', err);
        }
    }

    function handleEmailClick(email) {
        if (!selectedIds.includes(email.id)) {
            setSelectedIds([email.id]);
            setSelectedEmails([email]);
            fetchFullEmail(email.id);
        }
    }

    function handleToggleSelect(emailId) {
        setSelectedIds(prev => {
            const next = prev.includes(emailId)
                ? prev.filter(id => id !== emailId)
                : [...prev, emailId];
            setSelectedEmails(emails.filter(e => next.includes(e.id)));
            // Fetch full content for newly selected email
            if (!prev.includes(emailId)) {
                fetchFullEmail(emailId);
            }
            return next;
        });
    }

    async function handleAnalyze(params) {
        setAnalyzeLoading(true);
        try {
            const response = await apiPost('/outlook/analyze', params);
            if (response.ok) {
                return await response.json();
            }
            throw new Error('Analysis failed');
        } finally {
            setAnalyzeLoading(false);
        }
    }

    function handleSearch(e) {
        e.preventDefault();
        setSkip(0);
        fetchEmails(0);
    }

    function handleRefresh() {
        setSkip(0);
        fetchEmails(0);
    }

    function handleSearchSender(senderAddress) {
        const query = `from:${senderAddress}`;
        setSearchQuery(query);
        setSkip(0);
        setSelectedIds([]);
        setSelectedEmails([]);
        fetchEmails(0, query);
    }

    async function handleStatusChange(isConnected) {
        if (!isConnected) {
            try {
                await apiDelete('/outlook/disconnect');
            } catch (err) {
                console.error('Failed to disconnect:', err);
            }
            setEmails([]);
            setSelectedIds([]);
            setSelectedEmails([]);
            setFullEmails({});
        }
        setConnected(isConnected);
    }

    // Not authorized (non-zeffert or wrong role)
    if (authorized === false) {
        return (
            <div className={`flex items-center justify-center h-full ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
                <div className="text-center max-w-md px-6">
                    <AlertTriangle size={40} className="mx-auto mb-4 opacity-60" />
                    <h2 className={`text-lg font-dm-serif font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        Feature Restricted
                    </h2>
                    <p className="text-sm font-rasa">
                        Outlook email integration is available to authorized Zeffert team members.
                    </p>
                </div>
            </div>
        );
    }

    // Loading state
    if (authorized === null) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    // Not connected
    if (!connected) {
        const isZeffertEmail = currentUser?.email?.endsWith('@zeffert.com');

        return (
            <div className={`flex items-center justify-center h-full ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
                <div className="text-center max-w-md px-6">
                    <Inbox size={40} className="mx-auto mb-4 opacity-60" />
                    {isZeffertEmail ? (
                        <>
                            <h2 className={`text-lg font-dm-serif font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                Connect Your Outlook
                            </h2>
                            <p className="text-sm font-rasa mb-6">
                                Connect your Microsoft Outlook account to view, search, and analyze your emails with AI. No email data is stored.
                            </p>
                            <div className="flex justify-center">
                                <ConnectOutlookButton connected={false} onStatusChange={handleStatusChange} />
                            </div>
                        </>
                    ) : (
                        <>
                            <h2 className={`text-lg font-dm-serif font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                Outlook Integration Unavailable
                            </h2>
                            <p className="text-sm font-rasa">
                                This feature is available for Zeffert Microsoft accounts only.
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // Connected — main layout
    return (
        <div className="flex flex-col h-full overflow-hidden">
            {/* Top bar */}
            <div className={`flex items-center gap-3 px-4 py-2 border-b ${
                theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
            }`}>
                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className={`p-1.5 rounded-lg transition-colors ${
                        theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                    }`}
                    title={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
                >
                    {sidebarOpen ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
                </button>

                {/* Folder tabs */}
                <div className="flex gap-1">
                    {['inbox', 'sentitems', 'archive'].map(folder => {
                        const Icon = FOLDER_ICONS[folder] || FolderOpen;
                        const folderData = folders.find(f => f.displayName?.toLowerCase().replace(' ', '') === folder || f.id === folder);
                        return (
                            <button
                                key={folder}
                                onClick={() => { setCurrentFolder(folder); setSkip(0); }}
                                className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-sm font-rasa transition-colors ${
                                    currentFolder === folder
                                        ? theme === 'dark' ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-50 text-blue-600'
                                        : theme === 'dark' ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                                }`}
                            >
                                <Icon size={14} />
                                <span className="capitalize">{folder === 'sentitems' ? 'Sent' : folder}</span>
                                {folderData?.unreadItemCount > 0 && (
                                    <span className="bg-blue-600 text-white text-[10px] px-1.5 rounded-full">
                                        {folderData.unreadItemCount}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Search */}
                <form onSubmit={handleSearch} className="flex-1 max-w-md">
                    <div className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 ${
                        theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'
                    }`}>
                        <Search size={14} className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} />
                        <input
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search emails or from:user@example.com"
                            className={`flex-1 bg-transparent border-none outline-none text-xs font-rasa ${
                                theme === 'dark' ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
                            }`}
                        />
                    </div>
                </form>

                <button
                    onClick={handleRefresh}
                    disabled={emailsLoading}
                    className={`p-1.5 rounded-lg transition-colors ${
                        theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                    }`}
                >
                    <RefreshCw size={16} className={emailsLoading ? 'animate-spin' : ''} />
                </button>

                {/* Settings dropdown */}
                <div className="relative" ref={settingsRef}>
                    <button
                        onClick={() => setSettingsOpen(!settingsOpen)}
                        className={`p-1.5 rounded-lg transition-colors ${
                            theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                        }`}
                        title="Email settings"
                    >
                        <Settings size={16} />
                    </button>
                    {settingsOpen && (
                        <div className={`absolute right-0 top-full mt-1 w-48 rounded-lg shadow-lg border z-50 py-1 ${
                            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}>
                            <button
                                onClick={() => {
                                    setSettingsOpen(false);
                                    handleStatusChange(false);
                                }}
                                className={`flex items-center gap-2 w-full px-3 py-2 text-base font-rasa text-left transition-colors ${
                                    theme === 'dark'
                                        ? 'text-red-400 hover:bg-gray-700'
                                        : 'text-red-600 hover:bg-red-50'
                                }`}
                            >
                                <Unplug size={14} />
                                Disconnect Outlook
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Main content — sidebar + chat */}
            <div className="flex flex-1 overflow-hidden relative">
                {/* Email sidebar — animated slide */}
                <div
                    className={`flex-shrink-0 border-r overflow-hidden flex flex-col transition-all duration-300 ease-in-out ${
                        sidebarOpen ? 'w-80' : 'w-0 border-r-0'
                    } ${theme === 'dark' ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'}`}
                >
                    <div className="w-80 h-full flex flex-col">
                        <div className="flex-1 overflow-y-auto">
                            <EmailList
                                emails={emails}
                                selectedIds={selectedIds}
                                onToggleSelect={handleToggleSelect}
                                onEmailClick={handleEmailClick}
                                loading={emailsLoading}
                            />
                        </div>

                        {/* Pagination */}
                        {emails.length > 0 && (
                            <div className={`flex items-center justify-between px-3 py-2 border-t text-sm font-rasa flex-shrink-0 ${
                                theme === 'dark' ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'
                            }`}>
                                <button
                                    onClick={() => { const s = Math.max(0, skip - perPage); setSkip(s); fetchEmails(s); }}
                                    disabled={skip === 0}
                                    className="disabled:opacity-30"
                                >
                                    Previous
                                </button>
                                <span>{skip + 1}–{skip + emails.length}</span>
                                <button
                                    onClick={() => { const s = skip + perPage; setSkip(s); fetchEmails(s); }}
                                    disabled={emails.length < perPage}
                                    className="disabled:opacity-30"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sidebar toggle tab — visible when collapsed */}
                {!sidebarOpen && (
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-r-lg border border-l-0 transition-colors ${
                            theme === 'dark'
                                ? 'bg-gray-800 border-gray-700 hover:bg-gray-700 text-gray-400'
                                : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-500'
                        }`}
                        title="Show emails"
                    >
                        <ChevronRight size={16} />
                    </button>
                )}

                {/* Chat panel */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    <EmailAIChat
                        selectedEmails={selectedEmails}
                        fullEmails={fullEmails}
                        onAnalyze={handleAnalyze}
                        onSearchSender={handleSearchSender}
                        loading={analyzeLoading}
                    />
                </div>
            </div>
        </div>
    );
}
